import { useMutation, useQuery } from '@tanstack/react-query';
import type { MutationFunction, QueryFunction, QueryKey, UseMutationOptions, UseMutationResult, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import type {
  BrokerNerdsBarkerCoreModelsDTIBaseResponse,
  BrokerNerdsBarkerCoreModelsDTIBaseResponseWithMessage,
  BrokerNerdsBarkerCoreModelsDTIBulkEditListingRequest,
  BrokerNerdsBarkerCoreModelsDTIBulkEditStateRequest,
  BrokerNerdsBarkerCoreModelsDTIBulkListingsUpdateResponse,
  BrokerNerdsBarkerCoreModelsDTIBulkPurchaseRequest,
  BrokerNerdsBarkerCoreModelsDTIBulkResponseWithErrors,
  BrokerNerdsBarkerCoreModelsDTIBulkUpdateListingRequest,
  BrokerNerdsBarkerCoreModelsDTIDeleteListingRequest,
  BrokerNerdsBarkerCoreModelsDTIDownloadAirbillRequest,
  BrokerNerdsBarkerCoreModelsDTIEventIssueRequest,
  BrokerNerdsBarkerCoreModelsDTIGetAccountsResponse,
  BrokerNerdsBarkerCoreModelsDTIGetCategoriesResponse,
  BrokerNerdsBarkerCoreModelsDTIGetDetailsResponse,
  BrokerNerdsBarkerCoreModelsDTIGetEventListingsResponse,
  BrokerNerdsBarkerCoreModelsDTIGetEventsResponse,
  BrokerNerdsBarkerCoreModelsDTIGetHeadlinersResponse,
  BrokerNerdsBarkerCoreModelsDTIGetInvoicesRequest,
  BrokerNerdsBarkerCoreModelsDTIGetInvoicesResponse,
  BrokerNerdsBarkerCoreModelsDTIGetItemHistoryResponse,
  BrokerNerdsBarkerCoreModelsDTIGetItemsResponse,
  BrokerNerdsBarkerCoreModelsDTIGetSeatGeekMarketplaceDataResponse,
  BrokerNerdsBarkerCoreModelsDTIGetVenuesResponse,
  BrokerNerdsBarkerCoreModelsDTIListingIssueRequest,
  BrokerNerdsBarkerCoreModelsDTIReportMissingEventRequest,
  BrokerNerdsBarkerCoreModelsDTISellPrivatelyRequest,
  GetDtiEventsParams,
  GetDtiItemsAccountIdParams,
  PostDtiInvoiceAccountIdUploadProofBody,
  PostDtiPdfsAccountIdAttachBody,
} from './models';

export const getDtiListingsAccountIdEventId = (
  accountId: number,
  eventId: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIGetEventListingsResponse>> => {
  return axios.get(`/dti/listings/${accountId}/${eventId}`, options);
};

export const getGetDtiListingsAccountIdEventIdQueryKey = (accountId: number, eventId: number) => {
  return [`/dti/listings/${accountId}/${eventId}`] as const;
};

export const getGetDtiListingsAccountIdEventIdQueryOptions = <TData = Awaited<ReturnType<typeof getDtiListingsAccountIdEventId>>, TError = AxiosError<void>>(
  accountId: number,
  eventId: number,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiListingsAccountIdEventId>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDtiListingsAccountIdEventIdQueryKey(accountId, eventId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDtiListingsAccountIdEventId>>> = ({ signal }) =>
    getDtiListingsAccountIdEventId(accountId, eventId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!(accountId && eventId), ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getDtiListingsAccountIdEventId>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetDtiListingsAccountIdEventIdQueryResult = NonNullable<Awaited<ReturnType<typeof getDtiListingsAccountIdEventId>>>;
export type GetDtiListingsAccountIdEventIdQueryError = AxiosError<void>;

export const useGetDtiListingsAccountIdEventId = <TData = Awaited<ReturnType<typeof getDtiListingsAccountIdEventId>>, TError = AxiosError<void>>(
  accountId: number,
  eventId: number,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiListingsAccountIdEventId>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetDtiListingsAccountIdEventIdQueryOptions(accountId, eventId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const postDtiItemsAccountIdSellPrivately = (
  accountId: number,
  brokerNerdsBarkerCoreModelsDTISellPrivatelyRequest: BrokerNerdsBarkerCoreModelsDTISellPrivatelyRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIBaseResponse>> => {
  return axios.post(`/dti/items/${accountId}/sellPrivately`, brokerNerdsBarkerCoreModelsDTISellPrivatelyRequest, options);
};

export const getPostDtiItemsAccountIdSellPrivatelyMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postDtiItemsAccountIdSellPrivately>>,
    TError,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTISellPrivatelyRequest },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postDtiItemsAccountIdSellPrivately>>,
  TError,
  { accountId: number; data: BrokerNerdsBarkerCoreModelsDTISellPrivatelyRequest },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postDtiItemsAccountIdSellPrivately>>,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTISellPrivatelyRequest }
  > = (props) => {
    const { accountId, data } = props ?? {};

    return postDtiItemsAccountIdSellPrivately(accountId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostDtiItemsAccountIdSellPrivatelyMutationResult = NonNullable<Awaited<ReturnType<typeof postDtiItemsAccountIdSellPrivately>>>;
export type PostDtiItemsAccountIdSellPrivatelyMutationBody = BrokerNerdsBarkerCoreModelsDTISellPrivatelyRequest;
export type PostDtiItemsAccountIdSellPrivatelyMutationError = AxiosError<void>;

export const usePostDtiItemsAccountIdSellPrivately = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postDtiItemsAccountIdSellPrivately>>,
    TError,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTISellPrivatelyRequest },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof postDtiItemsAccountIdSellPrivately>>,
  TError,
  { accountId: number; data: BrokerNerdsBarkerCoreModelsDTISellPrivatelyRequest },
  TContext
> => {
  const mutationOptions = getPostDtiItemsAccountIdSellPrivatelyMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getDtiItemsAccountId = (
  accountId: number,
  params?: GetDtiItemsAccountIdParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIGetItemsResponse>> => {
  return axios.get(`/dti/items/${accountId}`, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getGetDtiItemsAccountIdQueryKey = (accountId: number, params?: GetDtiItemsAccountIdParams) => {
  return [`/dti/items/${accountId}`, ...(params ? [params] : [])] as const;
};

export const getGetDtiItemsAccountIdQueryOptions = <TData = Awaited<ReturnType<typeof getDtiItemsAccountId>>, TError = AxiosError<void>>(
  accountId: number,
  params?: GetDtiItemsAccountIdParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiItemsAccountId>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDtiItemsAccountIdQueryKey(accountId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDtiItemsAccountId>>> = ({ signal }) => getDtiItemsAccountId(accountId, params, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!accountId, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getDtiItemsAccountId>>, TError, TData> & { queryKey: QueryKey };
};

export type GetDtiItemsAccountIdQueryResult = NonNullable<Awaited<ReturnType<typeof getDtiItemsAccountId>>>;
export type GetDtiItemsAccountIdQueryError = AxiosError<void>;

export const useGetDtiItemsAccountId = <TData = Awaited<ReturnType<typeof getDtiItemsAccountId>>, TError = AxiosError<void>>(
  accountId: number,
  params?: GetDtiItemsAccountIdParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiItemsAccountId>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetDtiItemsAccountIdQueryOptions(accountId, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const putDtiListingsAccountId = (
  accountId: number,
  brokerNerdsBarkerCoreModelsDTIBulkUpdateListingRequest: BrokerNerdsBarkerCoreModelsDTIBulkUpdateListingRequest[],
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIBulkListingsUpdateResponse>> => {
  return axios.put(`/dti/listings/${accountId}`, brokerNerdsBarkerCoreModelsDTIBulkUpdateListingRequest, options);
};

export const getPutDtiListingsAccountIdMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putDtiListingsAccountId>>,
    TError,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkUpdateListingRequest[] },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putDtiListingsAccountId>>,
  TError,
  { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkUpdateListingRequest[] },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof putDtiListingsAccountId>>, { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkUpdateListingRequest[] }> = (
    props,
  ) => {
    const { accountId, data } = props ?? {};

    return putDtiListingsAccountId(accountId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutDtiListingsAccountIdMutationResult = NonNullable<Awaited<ReturnType<typeof putDtiListingsAccountId>>>;
export type PutDtiListingsAccountIdMutationBody = BrokerNerdsBarkerCoreModelsDTIBulkUpdateListingRequest[];
export type PutDtiListingsAccountIdMutationError = AxiosError<void>;

export const usePutDtiListingsAccountId = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putDtiListingsAccountId>>,
    TError,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkUpdateListingRequest[] },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof putDtiListingsAccountId>>,
  TError,
  { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkUpdateListingRequest[] },
  TContext
> => {
  const mutationOptions = getPutDtiListingsAccountIdMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postDtiListingsAccountId = (
  accountId: number,
  brokerNerdsBarkerCoreModelsDTIBulkPurchaseRequest: BrokerNerdsBarkerCoreModelsDTIBulkPurchaseRequest[],
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIBulkResponseWithErrors>> => {
  return axios.post(`/dti/listings/${accountId}`, brokerNerdsBarkerCoreModelsDTIBulkPurchaseRequest, options);
};

export const getPostDtiListingsAccountIdMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postDtiListingsAccountId>>,
    TError,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkPurchaseRequest[] },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postDtiListingsAccountId>>,
  TError,
  { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkPurchaseRequest[] },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof postDtiListingsAccountId>>, { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkPurchaseRequest[] }> = (
    props,
  ) => {
    const { accountId, data } = props ?? {};

    return postDtiListingsAccountId(accountId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostDtiListingsAccountIdMutationResult = NonNullable<Awaited<ReturnType<typeof postDtiListingsAccountId>>>;
export type PostDtiListingsAccountIdMutationBody = BrokerNerdsBarkerCoreModelsDTIBulkPurchaseRequest[];
export type PostDtiListingsAccountIdMutationError = AxiosError<void>;

export const usePostDtiListingsAccountId = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postDtiListingsAccountId>>,
    TError,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkPurchaseRequest[] },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof postDtiListingsAccountId>>, TError, { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkPurchaseRequest[] }, TContext> => {
  const mutationOptions = getPostDtiListingsAccountIdMutationOptions(options);

  return useMutation(mutationOptions);
};
export const putDtiListingsAccountIdEditState = (
  accountId: number,
  brokerNerdsBarkerCoreModelsDTIBulkEditStateRequest: BrokerNerdsBarkerCoreModelsDTIBulkEditStateRequest[],
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIBulkListingsUpdateResponse>> => {
  return axios.put(`/dti/listings/${accountId}/edit/state`, brokerNerdsBarkerCoreModelsDTIBulkEditStateRequest, options);
};

export const getPutDtiListingsAccountIdEditStateMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putDtiListingsAccountIdEditState>>,
    TError,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkEditStateRequest[] },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putDtiListingsAccountIdEditState>>,
  TError,
  { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkEditStateRequest[] },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putDtiListingsAccountIdEditState>>,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkEditStateRequest[] }
  > = (props) => {
    const { accountId, data } = props ?? {};

    return putDtiListingsAccountIdEditState(accountId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutDtiListingsAccountIdEditStateMutationResult = NonNullable<Awaited<ReturnType<typeof putDtiListingsAccountIdEditState>>>;
export type PutDtiListingsAccountIdEditStateMutationBody = BrokerNerdsBarkerCoreModelsDTIBulkEditStateRequest[];
export type PutDtiListingsAccountIdEditStateMutationError = AxiosError<void>;

export const usePutDtiListingsAccountIdEditState = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putDtiListingsAccountIdEditState>>,
    TError,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkEditStateRequest[] },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof putDtiListingsAccountIdEditState>>,
  TError,
  { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkEditStateRequest[] },
  TContext
> => {
  const mutationOptions = getPutDtiListingsAccountIdEditStateMutationOptions(options);

  return useMutation(mutationOptions);
};
export const putDtiListingsAccountIdEditListing = (
  accountId: number,
  brokerNerdsBarkerCoreModelsDTIBulkEditListingRequest: BrokerNerdsBarkerCoreModelsDTIBulkEditListingRequest[],
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIBulkListingsUpdateResponse>> => {
  return axios.put(`/dti/listings/${accountId}/edit/listing`, brokerNerdsBarkerCoreModelsDTIBulkEditListingRequest, options);
};

export const getPutDtiListingsAccountIdEditListingMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putDtiListingsAccountIdEditListing>>,
    TError,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkEditListingRequest[] },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putDtiListingsAccountIdEditListing>>,
  TError,
  { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkEditListingRequest[] },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putDtiListingsAccountIdEditListing>>,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkEditListingRequest[] }
  > = (props) => {
    const { accountId, data } = props ?? {};

    return putDtiListingsAccountIdEditListing(accountId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutDtiListingsAccountIdEditListingMutationResult = NonNullable<Awaited<ReturnType<typeof putDtiListingsAccountIdEditListing>>>;
export type PutDtiListingsAccountIdEditListingMutationBody = BrokerNerdsBarkerCoreModelsDTIBulkEditListingRequest[];
export type PutDtiListingsAccountIdEditListingMutationError = AxiosError<void>;

export const usePutDtiListingsAccountIdEditListing = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putDtiListingsAccountIdEditListing>>,
    TError,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkEditListingRequest[] },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof putDtiListingsAccountIdEditListing>>,
  TError,
  { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIBulkEditListingRequest[] },
  TContext
> => {
  const mutationOptions = getPutDtiListingsAccountIdEditListingMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postDtiEventsReportMissing = (
  brokerNerdsBarkerCoreModelsDTIReportMissingEventRequest: BrokerNerdsBarkerCoreModelsDTIReportMissingEventRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIBaseResponseWithMessage>> => {
  return axios.post(`/dti/events/report_missing`, brokerNerdsBarkerCoreModelsDTIReportMissingEventRequest, options);
};

export const getPostDtiEventsReportMissingMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postDtiEventsReportMissing>>, TError, { data: BrokerNerdsBarkerCoreModelsDTIReportMissingEventRequest }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof postDtiEventsReportMissing>>, TError, { data: BrokerNerdsBarkerCoreModelsDTIReportMissingEventRequest }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof postDtiEventsReportMissing>>, { data: BrokerNerdsBarkerCoreModelsDTIReportMissingEventRequest }> = (props) => {
    const { data } = props ?? {};

    return postDtiEventsReportMissing(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostDtiEventsReportMissingMutationResult = NonNullable<Awaited<ReturnType<typeof postDtiEventsReportMissing>>>;
export type PostDtiEventsReportMissingMutationBody = BrokerNerdsBarkerCoreModelsDTIReportMissingEventRequest;
export type PostDtiEventsReportMissingMutationError = AxiosError<void>;

export const usePostDtiEventsReportMissing = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postDtiEventsReportMissing>>, TError, { data: BrokerNerdsBarkerCoreModelsDTIReportMissingEventRequest }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof postDtiEventsReportMissing>>, TError, { data: BrokerNerdsBarkerCoreModelsDTIReportMissingEventRequest }, TContext> => {
  const mutationOptions = getPostDtiEventsReportMissingMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getDtiHeadliners = (options?: AxiosRequestConfig): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIGetHeadlinersResponse>> => {
  return axios.get(`/dti/headliners`, options);
};

export const getGetDtiHeadlinersQueryKey = () => {
  return [`/dti/headliners`] as const;
};

export const getGetDtiHeadlinersQueryOptions = <TData = Awaited<ReturnType<typeof getDtiHeadliners>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiHeadliners>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDtiHeadlinersQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDtiHeadliners>>> = ({ signal }) => getDtiHeadliners({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getDtiHeadliners>>, TError, TData> & { queryKey: QueryKey };
};

export type GetDtiHeadlinersQueryResult = NonNullable<Awaited<ReturnType<typeof getDtiHeadliners>>>;
export type GetDtiHeadlinersQueryError = AxiosError<void>;

export const useGetDtiHeadliners = <TData = Awaited<ReturnType<typeof getDtiHeadliners>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiHeadliners>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetDtiHeadlinersQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getDtiVenues = (options?: AxiosRequestConfig): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIGetVenuesResponse>> => {
  return axios.get(`/dti/venues`, options);
};

export const getGetDtiVenuesQueryKey = () => {
  return [`/dti/venues`] as const;
};

export const getGetDtiVenuesQueryOptions = <TData = Awaited<ReturnType<typeof getDtiVenues>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiVenues>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDtiVenuesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDtiVenues>>> = ({ signal }) => getDtiVenues({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getDtiVenues>>, TError, TData> & { queryKey: QueryKey };
};

export type GetDtiVenuesQueryResult = NonNullable<Awaited<ReturnType<typeof getDtiVenues>>>;
export type GetDtiVenuesQueryError = AxiosError<void>;

export const useGetDtiVenues = <TData = Awaited<ReturnType<typeof getDtiVenues>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiVenues>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetDtiVenuesQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getDtiCategories = (options?: AxiosRequestConfig): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIGetCategoriesResponse>> => {
  return axios.get(`/dti/categories`, options);
};

export const getGetDtiCategoriesQueryKey = () => {
  return [`/dti/categories`] as const;
};

export const getGetDtiCategoriesQueryOptions = <TData = Awaited<ReturnType<typeof getDtiCategories>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiCategories>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDtiCategoriesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDtiCategories>>> = ({ signal }) => getDtiCategories({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getDtiCategories>>, TError, TData> & { queryKey: QueryKey };
};

export type GetDtiCategoriesQueryResult = NonNullable<Awaited<ReturnType<typeof getDtiCategories>>>;
export type GetDtiCategoriesQueryError = AxiosError<void>;

export const useGetDtiCategories = <TData = Awaited<ReturnType<typeof getDtiCategories>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiCategories>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetDtiCategoriesQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getDtiEvents = (params: GetDtiEventsParams, options?: AxiosRequestConfig): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIGetEventsResponse>> => {
  return axios.get(`/dti/events`, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getGetDtiEventsQueryKey = (params: GetDtiEventsParams) => {
  return [`/dti/events`, ...(params ? [params] : [])] as const;
};

export const getGetDtiEventsQueryOptions = <TData = Awaited<ReturnType<typeof getDtiEvents>>, TError = AxiosError<void>>(
  params: GetDtiEventsParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiEvents>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDtiEventsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDtiEvents>>> = ({ signal }) => getDtiEvents(params, { signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getDtiEvents>>, TError, TData> & { queryKey: QueryKey };
};

export type GetDtiEventsQueryResult = NonNullable<Awaited<ReturnType<typeof getDtiEvents>>>;
export type GetDtiEventsQueryError = AxiosError<void>;

export const useGetDtiEvents = <TData = Awaited<ReturnType<typeof getDtiEvents>>, TError = AxiosError<void>>(
  params: GetDtiEventsParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiEvents>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetDtiEventsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getDtiEventsEventIdMarketplaceSeatgeek = (
  eventId: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIGetSeatGeekMarketplaceDataResponse>> => {
  return axios.get(`/dti/events/${eventId}/marketplace/seatgeek`, options);
};

export const getGetDtiEventsEventIdMarketplaceSeatgeekQueryKey = (eventId: number) => {
  return [`/dti/events/${eventId}/marketplace/seatgeek`] as const;
};

export const getGetDtiEventsEventIdMarketplaceSeatgeekQueryOptions = <TData = Awaited<ReturnType<typeof getDtiEventsEventIdMarketplaceSeatgeek>>, TError = AxiosError<void>>(
  eventId: number,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiEventsEventIdMarketplaceSeatgeek>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDtiEventsEventIdMarketplaceSeatgeekQueryKey(eventId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDtiEventsEventIdMarketplaceSeatgeek>>> = ({ signal }) =>
    getDtiEventsEventIdMarketplaceSeatgeek(eventId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!eventId, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getDtiEventsEventIdMarketplaceSeatgeek>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetDtiEventsEventIdMarketplaceSeatgeekQueryResult = NonNullable<Awaited<ReturnType<typeof getDtiEventsEventIdMarketplaceSeatgeek>>>;
export type GetDtiEventsEventIdMarketplaceSeatgeekQueryError = AxiosError<void>;

export const useGetDtiEventsEventIdMarketplaceSeatgeek = <TData = Awaited<ReturnType<typeof getDtiEventsEventIdMarketplaceSeatgeek>>, TError = AxiosError<void>>(
  eventId: number,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiEventsEventIdMarketplaceSeatgeek>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetDtiEventsEventIdMarketplaceSeatgeekQueryOptions(eventId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const postDtiListingsAccountIdDelete = (
  accountId: number,
  brokerNerdsBarkerCoreModelsDTIDeleteListingRequest: BrokerNerdsBarkerCoreModelsDTIDeleteListingRequest[],
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIBaseResponseWithMessage>> => {
  return axios.post(`/dti/listings/${accountId}/delete`, brokerNerdsBarkerCoreModelsDTIDeleteListingRequest, options);
};

export const getPostDtiListingsAccountIdDeleteMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postDtiListingsAccountIdDelete>>,
    TError,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIDeleteListingRequest[] },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postDtiListingsAccountIdDelete>>,
  TError,
  { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIDeleteListingRequest[] },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postDtiListingsAccountIdDelete>>,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIDeleteListingRequest[] }
  > = (props) => {
    const { accountId, data } = props ?? {};

    return postDtiListingsAccountIdDelete(accountId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostDtiListingsAccountIdDeleteMutationResult = NonNullable<Awaited<ReturnType<typeof postDtiListingsAccountIdDelete>>>;
export type PostDtiListingsAccountIdDeleteMutationBody = BrokerNerdsBarkerCoreModelsDTIDeleteListingRequest[];
export type PostDtiListingsAccountIdDeleteMutationError = AxiosError<void>;

export const usePostDtiListingsAccountIdDelete = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postDtiListingsAccountIdDelete>>,
    TError,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIDeleteListingRequest[] },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof postDtiListingsAccountIdDelete>>,
  TError,
  { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIDeleteListingRequest[] },
  TContext
> => {
  const mutationOptions = getPostDtiListingsAccountIdDeleteMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getDtiItemsAccountIdItemIdHistory = (
  accountId: number,
  itemId: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIGetItemHistoryResponse>> => {
  return axios.get(`/dti/items/${accountId}/${itemId}/history`, options);
};

export const getGetDtiItemsAccountIdItemIdHistoryQueryKey = (accountId: number, itemId: number) => {
  return [`/dti/items/${accountId}/${itemId}/history`] as const;
};

export const getGetDtiItemsAccountIdItemIdHistoryQueryOptions = <TData = Awaited<ReturnType<typeof getDtiItemsAccountIdItemIdHistory>>, TError = AxiosError<void>>(
  accountId: number,
  itemId: number,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiItemsAccountIdItemIdHistory>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDtiItemsAccountIdItemIdHistoryQueryKey(accountId, itemId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDtiItemsAccountIdItemIdHistory>>> = ({ signal }) =>
    getDtiItemsAccountIdItemIdHistory(accountId, itemId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!(accountId && itemId), ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getDtiItemsAccountIdItemIdHistory>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetDtiItemsAccountIdItemIdHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof getDtiItemsAccountIdItemIdHistory>>>;
export type GetDtiItemsAccountIdItemIdHistoryQueryError = AxiosError<void>;

export const useGetDtiItemsAccountIdItemIdHistory = <TData = Awaited<ReturnType<typeof getDtiItemsAccountIdItemIdHistory>>, TError = AxiosError<void>>(
  accountId: number,
  itemId: number,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiItemsAccountIdItemIdHistory>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetDtiItemsAccountIdItemIdHistoryQueryOptions(accountId, itemId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const postDtiListingsAccountIdIssue = (
  accountId: number,
  brokerNerdsBarkerCoreModelsDTIListingIssueRequest: BrokerNerdsBarkerCoreModelsDTIListingIssueRequest[],
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIBaseResponseWithMessage>> => {
  return axios.post(`/dti/listings/${accountId}/issue`, brokerNerdsBarkerCoreModelsDTIListingIssueRequest, options);
};

export const getPostDtiListingsAccountIdIssueMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postDtiListingsAccountIdIssue>>,
    TError,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIListingIssueRequest[] },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postDtiListingsAccountIdIssue>>,
  TError,
  { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIListingIssueRequest[] },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postDtiListingsAccountIdIssue>>,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIListingIssueRequest[] }
  > = (props) => {
    const { accountId, data } = props ?? {};

    return postDtiListingsAccountIdIssue(accountId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostDtiListingsAccountIdIssueMutationResult = NonNullable<Awaited<ReturnType<typeof postDtiListingsAccountIdIssue>>>;
export type PostDtiListingsAccountIdIssueMutationBody = BrokerNerdsBarkerCoreModelsDTIListingIssueRequest[];
export type PostDtiListingsAccountIdIssueMutationError = AxiosError<void>;

export const usePostDtiListingsAccountIdIssue = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postDtiListingsAccountIdIssue>>,
    TError,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIListingIssueRequest[] },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof postDtiListingsAccountIdIssue>>,
  TError,
  { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIListingIssueRequest[] },
  TContext
> => {
  const mutationOptions = getPostDtiListingsAccountIdIssueMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getDtiAccounts = (options?: AxiosRequestConfig): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIGetAccountsResponse>> => {
  return axios.get(`/dti/accounts`, options);
};

export const getGetDtiAccountsQueryKey = () => {
  return [`/dti/accounts`] as const;
};

export const getGetDtiAccountsQueryOptions = <TData = Awaited<ReturnType<typeof getDtiAccounts>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiAccounts>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDtiAccountsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDtiAccounts>>> = ({ signal }) => getDtiAccounts({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getDtiAccounts>>, TError, TData> & { queryKey: QueryKey };
};

export type GetDtiAccountsQueryResult = NonNullable<Awaited<ReturnType<typeof getDtiAccounts>>>;
export type GetDtiAccountsQueryError = AxiosError<void>;

export const useGetDtiAccounts = <TData = Awaited<ReturnType<typeof getDtiAccounts>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiAccounts>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetDtiAccountsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const postDtiEventsEventIdIssue = (
  eventId: number,
  brokerNerdsBarkerCoreModelsDTIEventIssueRequest: BrokerNerdsBarkerCoreModelsDTIEventIssueRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIBaseResponseWithMessage>> => {
  return axios.post(`/dti/events/${eventId}/issue`, brokerNerdsBarkerCoreModelsDTIEventIssueRequest, options);
};

export const getPostDtiEventsEventIdIssueMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postDtiEventsEventIdIssue>>,
    TError,
    { eventId: number; data: BrokerNerdsBarkerCoreModelsDTIEventIssueRequest },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof postDtiEventsEventIdIssue>>, TError, { eventId: number; data: BrokerNerdsBarkerCoreModelsDTIEventIssueRequest }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof postDtiEventsEventIdIssue>>, { eventId: number; data: BrokerNerdsBarkerCoreModelsDTIEventIssueRequest }> = (
    props,
  ) => {
    const { eventId, data } = props ?? {};

    return postDtiEventsEventIdIssue(eventId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostDtiEventsEventIdIssueMutationResult = NonNullable<Awaited<ReturnType<typeof postDtiEventsEventIdIssue>>>;
export type PostDtiEventsEventIdIssueMutationBody = BrokerNerdsBarkerCoreModelsDTIEventIssueRequest;
export type PostDtiEventsEventIdIssueMutationError = AxiosError<void>;

export const usePostDtiEventsEventIdIssue = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postDtiEventsEventIdIssue>>,
    TError,
    { eventId: number; data: BrokerNerdsBarkerCoreModelsDTIEventIssueRequest },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof postDtiEventsEventIdIssue>>, TError, { eventId: number; data: BrokerNerdsBarkerCoreModelsDTIEventIssueRequest }, TContext> => {
  const mutationOptions = getPostDtiEventsEventIdIssueMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postDtiInvoiceAccountId = (
  accountId: number,
  brokerNerdsBarkerCoreModelsDTIGetInvoicesRequest: BrokerNerdsBarkerCoreModelsDTIGetInvoicesRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIGetInvoicesResponse>> => {
  return axios.post(`/dti/invoice/${accountId}`, brokerNerdsBarkerCoreModelsDTIGetInvoicesRequest, options);
};

export const getPostDtiInvoiceAccountIdMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postDtiInvoiceAccountId>>,
    TError,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIGetInvoicesRequest },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof postDtiInvoiceAccountId>>, TError, { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIGetInvoicesRequest }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof postDtiInvoiceAccountId>>, { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIGetInvoicesRequest }> = (
    props,
  ) => {
    const { accountId, data } = props ?? {};

    return postDtiInvoiceAccountId(accountId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostDtiInvoiceAccountIdMutationResult = NonNullable<Awaited<ReturnType<typeof postDtiInvoiceAccountId>>>;
export type PostDtiInvoiceAccountIdMutationBody = BrokerNerdsBarkerCoreModelsDTIGetInvoicesRequest;
export type PostDtiInvoiceAccountIdMutationError = AxiosError<void>;

export const usePostDtiInvoiceAccountId = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postDtiInvoiceAccountId>>,
    TError,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIGetInvoicesRequest },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof postDtiInvoiceAccountId>>, TError, { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIGetInvoicesRequest }, TContext> => {
  const mutationOptions = getPostDtiInvoiceAccountIdMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postDtiInvoiceAccountIdAirbillDownload = (
  accountId: number,
  brokerNerdsBarkerCoreModelsDTIDownloadAirbillRequest: BrokerNerdsBarkerCoreModelsDTIDownloadAirbillRequest[],
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIBaseResponseWithMessage>> => {
  return axios.post(`/dti/invoice/${accountId}/airbill/download`, brokerNerdsBarkerCoreModelsDTIDownloadAirbillRequest, options);
};

export const getPostDtiInvoiceAccountIdAirbillDownloadMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postDtiInvoiceAccountIdAirbillDownload>>,
    TError,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIDownloadAirbillRequest[] },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postDtiInvoiceAccountIdAirbillDownload>>,
  TError,
  { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIDownloadAirbillRequest[] },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postDtiInvoiceAccountIdAirbillDownload>>,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIDownloadAirbillRequest[] }
  > = (props) => {
    const { accountId, data } = props ?? {};

    return postDtiInvoiceAccountIdAirbillDownload(accountId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostDtiInvoiceAccountIdAirbillDownloadMutationResult = NonNullable<Awaited<ReturnType<typeof postDtiInvoiceAccountIdAirbillDownload>>>;
export type PostDtiInvoiceAccountIdAirbillDownloadMutationBody = BrokerNerdsBarkerCoreModelsDTIDownloadAirbillRequest[];
export type PostDtiInvoiceAccountIdAirbillDownloadMutationError = AxiosError<void>;

export const usePostDtiInvoiceAccountIdAirbillDownload = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postDtiInvoiceAccountIdAirbillDownload>>,
    TError,
    { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIDownloadAirbillRequest[] },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof postDtiInvoiceAccountIdAirbillDownload>>,
  TError,
  { accountId: number; data: BrokerNerdsBarkerCoreModelsDTIDownloadAirbillRequest[] },
  TContext
> => {
  const mutationOptions = getPostDtiInvoiceAccountIdAirbillDownloadMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postDtiPdfsAccountIdAttach = (
  accountId: number,
  postDtiPdfsAccountIdAttachBody: PostDtiPdfsAccountIdAttachBody,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIBaseResponseWithMessage>> => {
  const formData = new FormData();
  formData.append('metadata', postDtiPdfsAccountIdAttachBody.metadata);
  formData.append('file', postDtiPdfsAccountIdAttachBody.file);

  return axios.post(`/dti/pdfs/${accountId}/attach`, formData, options);
};

export const getPostDtiPdfsAccountIdAttachMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postDtiPdfsAccountIdAttach>>, TError, { accountId: number; data: PostDtiPdfsAccountIdAttachBody }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof postDtiPdfsAccountIdAttach>>, TError, { accountId: number; data: PostDtiPdfsAccountIdAttachBody }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof postDtiPdfsAccountIdAttach>>, { accountId: number; data: PostDtiPdfsAccountIdAttachBody }> = (props) => {
    const { accountId, data } = props ?? {};

    return postDtiPdfsAccountIdAttach(accountId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostDtiPdfsAccountIdAttachMutationResult = NonNullable<Awaited<ReturnType<typeof postDtiPdfsAccountIdAttach>>>;
export type PostDtiPdfsAccountIdAttachMutationBody = PostDtiPdfsAccountIdAttachBody;
export type PostDtiPdfsAccountIdAttachMutationError = AxiosError<void>;

export const usePostDtiPdfsAccountIdAttach = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postDtiPdfsAccountIdAttach>>, TError, { accountId: number; data: PostDtiPdfsAccountIdAttachBody }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof postDtiPdfsAccountIdAttach>>, TError, { accountId: number; data: PostDtiPdfsAccountIdAttachBody }, TContext> => {
  const mutationOptions = getPostDtiPdfsAccountIdAttachMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postDtiInvoiceAccountIdUploadProof = (
  accountId: number,
  postDtiInvoiceAccountIdUploadProofBody: PostDtiInvoiceAccountIdUploadProofBody,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIBaseResponseWithMessage>> => {
  const formData = new FormData();
  formData.append('invoice_id', postDtiInvoiceAccountIdUploadProofBody.invoice_id);
  formData.append('vendor_id', postDtiInvoiceAccountIdUploadProofBody.vendor_id);
  formData.append('file', postDtiInvoiceAccountIdUploadProofBody.file);

  return axios.post(`/dti/invoice/${accountId}/upload-proof`, formData, options);
};

export const getPostDtiInvoiceAccountIdUploadProofMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postDtiInvoiceAccountIdUploadProof>>,
    TError,
    { accountId: number; data: PostDtiInvoiceAccountIdUploadProofBody },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof postDtiInvoiceAccountIdUploadProof>>, TError, { accountId: number; data: PostDtiInvoiceAccountIdUploadProofBody }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof postDtiInvoiceAccountIdUploadProof>>, { accountId: number; data: PostDtiInvoiceAccountIdUploadProofBody }> = (
    props,
  ) => {
    const { accountId, data } = props ?? {};

    return postDtiInvoiceAccountIdUploadProof(accountId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostDtiInvoiceAccountIdUploadProofMutationResult = NonNullable<Awaited<ReturnType<typeof postDtiInvoiceAccountIdUploadProof>>>;
export type PostDtiInvoiceAccountIdUploadProofMutationBody = PostDtiInvoiceAccountIdUploadProofBody;
export type PostDtiInvoiceAccountIdUploadProofMutationError = AxiosError<void>;

export const usePostDtiInvoiceAccountIdUploadProof = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postDtiInvoiceAccountIdUploadProof>>,
    TError,
    { accountId: number; data: PostDtiInvoiceAccountIdUploadProofBody },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof postDtiInvoiceAccountIdUploadProof>>, TError, { accountId: number; data: PostDtiInvoiceAccountIdUploadProofBody }, TContext> => {
  const mutationOptions = getPostDtiInvoiceAccountIdUploadProofMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getDtiDetails = (options?: AxiosRequestConfig): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsDTIGetDetailsResponse>> => {
  return axios.get(`/dti/details`, options);
};

export const getGetDtiDetailsQueryKey = () => {
  return [`/dti/details`] as const;
};

export const getGetDtiDetailsQueryOptions = <TData = Awaited<ReturnType<typeof getDtiDetails>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiDetails>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDtiDetailsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDtiDetails>>> = ({ signal }) => getDtiDetails({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getDtiDetails>>, TError, TData> & { queryKey: QueryKey };
};

export type GetDtiDetailsQueryResult = NonNullable<Awaited<ReturnType<typeof getDtiDetails>>>;
export type GetDtiDetailsQueryError = AxiosError<void>;

export const useGetDtiDetails = <TData = Awaited<ReturnType<typeof getDtiDetails>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDtiDetails>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetDtiDetailsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};
